import { useCallback } from "react";
import { useDataContext } from "../DataContext";
import { useUIContext } from "../UIContext";

/**
 * Hook for managing course navigation operations
 * @returns {Object} Object containing navigation functions
 */
export const useCatalogueNavigation = () => {
  const dataContext = useDataContext();
  const uiContext = useUIContext();

  /**
   * Finds the index of the currently selected course
   * @returns {number} Index of the current course, or -1 if not found
   */
  const findCurrentCourseIndex = useCallback(() => {
    if (
      !uiContext.uiState.selectedActivity ||
      !dataContext.data.learningActivities
    ) {
      return -1;
    }
    return dataContext.data.learningActivities.courses.findIndex(
      (course) => course.id === uiContext.uiState.selectedActivity.id,
    );
  }, [uiContext.uiState.selectedActivity, dataContext.data.learningActivities]);

  /**
   * Checks if the current course is the first in the list
   * @returns {boolean} True if the current course is the first, false otherwise
   */
  const isFirstCourse = useCallback(() => {
    return findCurrentCourseIndex() === 0;
  }, [findCurrentCourseIndex]);

  /**
   * Checks if the current course is the last in the list
   * @returns {boolean} True if the current course is the last, false otherwise
   */
  const isLastCourse = useCallback(() => {
    if (!dataContext.data.learningActivities) return true;
    return (
      findCurrentCourseIndex() ===
      dataContext.data.learningActivities.courses.length - 1
    );
  }, [findCurrentCourseIndex, dataContext.data.learningActivities]);

  /**
   * Navigates to the previous course in the list
   */
  const navigateToPreviousCourse = useCallback(() => {
    const currentIndex = findCurrentCourseIndex();
    if (currentIndex > 0 && dataContext.data.learningActivities) {
      const previousCourse =
        dataContext.data.learningActivities.courses[currentIndex - 1];
      uiContext.updateUIState({ selectedActivity: previousCourse });
    }
  }, [findCurrentCourseIndex, dataContext.data.learningActivities, uiContext]);

  /**
   * Navigates to the next course in the list
   */
  const navigateToNextCourse = useCallback(() => {
    const currentIndex = findCurrentCourseIndex();
    if (
      dataContext.data.learningActivities &&
      currentIndex < dataContext.data.learningActivities.courses.length - 1
    ) {
      const nextCourse =
        dataContext.data.learningActivities.courses[currentIndex + 1];
      uiContext.updateUIState({ selectedActivity: nextCourse });
    }
  }, [findCurrentCourseIndex, dataContext.data.learningActivities, uiContext]);

  return {
    findCurrentCourseIndex,
    isFirstCourse,
    isLastCourse,
    navigateToPreviousCourse,
    navigateToNextCourse,
  };
};
